/**
 * Route guard. Redirects to `/login` when the user is not authenticated.
 * Works both as a wrapper (`<ProtectedRoute><X/></ProtectedRoute>`) and as a
 * layout route element (renders `<Outlet/>` when no children are supplied).
 */
import type { ReactElement, ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';

export interface ProtectedRouteProps {
  children?: ReactNode;
  redirectTo?: string;
}

/** Renders its children/outlet only when authenticated; otherwise redirects. */
export default function ProtectedRoute({
  children,
  redirectTo = '/login',
}: ProtectedRouteProps): ReactElement {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const location = useLocation();

  if (!isAuthenticated) {
    // Preserve the attempted location so the login page can redirect back.
    return <Navigate to={redirectTo} replace state={{ from: location }} />;
  }

  return <>{children ?? <Outlet />}</>;
}
